import React, { useState, useEffect } from 'react';
import Card from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import Textarea from '../../components/ui/Textarea';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import { api } from '../../api/nocode';

export default function Settings() {
  const [settings, setSettings] = useState({});
  const [originalSettings, setOriginalSettings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  const regions = [
    'us-east-1', 'us-east-2', 'us-west-1', 'us-west-2',
    'eu-west-1', 'eu-west-2', 'eu-central-1',
    'ap-southeast-1', 'ap-southeast-2', 'ap-northeast-1'
  ];

  const loadSettings = async () => {
    try {
      const res = await api.get('settings_fbwm');
      const data = Array.isArray(res) ? res : res.data || [];
      setOriginalSettings(data);
      const map = {};
      data.forEach(item => {
        if (item.setting_key) map[item.setting_key] = item.value;
      });
      // Ensure default webhook secret exists visually
      if (!map.webhook_secret) map.webhook_secret = 'sk_' + crypto.randomUUID().replace(/-/g, '').substring(0, 16);
      setSettings(map);
    } catch (e) {
      console.error("Failed to load settings", e);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setToast('');
    try {
      const promises = [];
      for (const [key, value] of Object.entries(settings)) {
        const orig = originalSettings.find(s => s.setting_key === key);
        const strValue = String(value);
        if (orig) {
          if (orig.value !== strValue) {
            promises.push(api.put('settings_fbwm', orig.id, { setting_key: key, value: strValue, owner_id: 'admin' }));
          }
        } else {
          if (strValue !== undefined && strValue !== '') {
            promises.push(api.post('settings_fbwm', { id: crypto.randomUUID(), setting_key: key, value: strValue, owner_id: 'admin' }));
          }
        }
      }
      await Promise.all(promises);
      setToast('Settings saved successfully!');
      await loadSettings(); 
    } catch (e) {
      console.error(e);
      setToast('Error saving settings. Please try again.');
    } finally {
      setSaving(false);
      setTimeout(() => setToast(''), 3000);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Platform Settings</h1>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save All Settings'}
        </Button>
      </div>

      {toast && (
        <div className={`px-4 py-3 rounded border ${toast.includes('Error') ? 'bg-red-100 border-red-400 text-red-700' : 'bg-green-100 border-green-400 text-green-700'}`}>
          {toast}
        </div>
      )}

      {/* REGISTRATION TOGGLE */}
      <Card className="space-y-6">
        <h2 className="text-lg font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
          <SafeIcon name="Users" /> Public Registration
        </h2>
        <div className="p-4 border rounded-md bg-gray-50 flex items-center justify-between">
          <div>
            <h4 className="font-bold text-dark-green">Enable Registration</h4>
            <p className="text-xs text-muted">When OFF, the registration page will show a closed message and redirect to the waitlist.</p>
          </div>
          <Button 
            variant={settings.registration_open !== 'false' ? 'primary' : 'outline'}
            onClick={() => handleChange('registration_open', settings.registration_open === 'false' ? 'true' : 'false')}
            className={settings.registration_open !== 'false' ? 'bg-forest-green text-white border-forest-green' : 'text-gray-500'}
          >
            {settings.registration_open !== 'false' ? 'ON' : 'OFF'}
          </Button>
        </div>
      </Card>

      {/* LETTERS TOGGLE */}
      <Card className="space-y-6">
        <h2 className="text-lg font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
          <SafeIcon name="Mail" /> Self-Letter Writing Module
        </h2>
        <div className="p-4 border rounded-md bg-gray-50 flex items-center justify-between">
          <div>
            <h4 className="font-bold text-dark-green">Enable Self-Letter Module</h4>
            <p className="text-xs text-muted">When OFF, the letters module will be locked for all campers.</p>
          </div>
          <Button 
            variant={settings.letters_module_open !== 'false' ? 'primary' : 'outline'}
            onClick={() => handleChange('letters_module_open', settings.letters_module_open === 'false' ? 'true' : 'false')}
            className={settings.letters_module_open !== 'false' ? 'bg-forest-green text-white border-forest-green' : 'text-gray-500'}
          >
            {settings.letters_module_open !== 'false' ? 'ON' : 'OFF'}
          </Button>
        </div>
      </Card>

      {/* DASHBOARD MESSAGES */}
      <Card className="space-y-6">
        <h2 className="text-lg font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
          <SafeIcon name="MessageSquare" /> Dashboard Messages
        </h2>
        <div className="space-y-4">
          <Textarea 
            label="Camp Closed Message (All Campers)" 
            value={settings.camp_closed_msg || ''} 
            onChange={e => handleChange('camp_closed_msg', e.target.value)} 
            placeholder="Message displayed to all campers after camp ends, until replay expires."
          />
          <Textarea 
            label="All-Access Welcome Message (Post-Camp)" 
            value={settings.aa_welcome_msg || ''} 
            onChange={e => handleChange('aa_welcome_msg', e.target.value)} 
            placeholder="Message displayed to All-Access members after camp ends if 'Camp Closed Message' is empty."
          />
          <Textarea 
            label="Replay Expiry Message (Lockout Screen)" 
            value={settings.replay_expiry_msg || ''} 
            onChange={e => handleChange('replay_expiry_msg', e.target.value)} 
            placeholder="Message displayed to free pass users when replay access has expired."
          />
        </div>
      </Card>

      <Card className="space-y-6">
        <h2 className="text-lg font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
          <SafeIcon name="Mail" /> AWS SES Configuration
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Input label="AWS Access Key" type="text" value={settings.ses_access_key || ''} onChange={e => handleChange('ses_access_key', e.target.value)} />
          <Input label="AWS Secret Key" type="password" value={settings.ses_secret_key || ''} onChange={e => handleChange('ses_secret_key', e.target.value)} />
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-dark-green">AWS Region</label>
            <select
              className="px-4 py-2 rounded-md border border-amber bg-cream focus:outline-none focus:ring-2 focus:ring-forest-green"
              value={settings.ses_region || 'us-east-1'}
              onChange={e => handleChange('ses_region', e.target.value)}
            >
              {regions.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <Input label="From Email" value={settings.ses_from_email || ''} onChange={e => handleChange('ses_from_email', e.target.value)} />
          <Input label="Display Name" value={settings.ses_display_name || ''} onChange={e => handleChange('ses_display_name', e.target.value)} />
        </div>
      </Card>

      <Card className="space-y-6">
        <h2 className="text-lg font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
          <SafeIcon name="Link" /> Skillplate Webhook Configuration
        </h2>
        <div className="space-y-4">
          <Input label="Webhook Secret Key" type="text" value={settings.webhook_secret || ''} onChange={e => handleChange('webhook_secret', e.target.value)} className="font-mono text-sm" />
          <Input label="Skillplate Product Name" value={settings.skillplate_product || 'Camp FaiBoWriMo 2026 All-Access Pass'} onChange={e => handleChange('skillplate_product', e.target.value)} />
          <Input 
            label="Webhook URL (Copy to Skillplate)" 
            value={`https://assets.emmausway.co/camp_fbwm/webhook-receiver.php?secret=${settings.webhook_secret || ''}`} 
            readOnly 
            className="bg-gray-50 font-mono text-xs" 
          />
          <p className="text-xs text-muted">Paste this exact URL into your Skillplate webhook settings. It contains the secret required to authenticate requests.</p>
        </div>
      </Card>

      <Card className="space-y-6">
        <h2 className="text-lg font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
          <SafeIcon name="Calendar" /> Camp Dates Configuration
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Input label="Camp Start Date" type="date" value={settings.camp_start || ''} onChange={e => handleChange('camp_start', e.target.value)} />
          <Input label="Camp End Date" type="date" value={settings.camp_end || ''} onChange={e => handleChange('camp_end', e.target.value)} />
          <Input label="Early Bird Deadline" type="datetime-local" value={settings.early_bird_deadline || ''} onChange={e => handleChange('early_bird_deadline', e.target.value)} />
          <Input label="Replay Expiry (Live Camp)" type="datetime-local" value={settings.replay_expiry || ''} onChange={e => handleChange('replay_expiry', e.target.value)} />
          <Input label="Evergreen Replay Window (Days)" type="number" value={settings.evergreen_replay_days || '14'} onChange={e => handleChange('evergreen_replay_days', e.target.value)} />
        </div>
      </Card>
    </div>
  );
}